/**
 * #330 — the three direction fields, shared by the fleet map's confirm box and the camera edit sheet: the
 * heading with the 8 compass presets, the width and the range. Text in, text out: the caller checks the values
 * with parseDirection (lib/mapDirection) and decides what an empty field means.
 * Fix round (controller ruling I-2) — a small compass beside the width and the range shows the wedge as typed,
 * wherever the fields are (on the phone the sheet is the only editor, with no map beside it). `required` says
 * whether an empty field is not valid (the confirm box) or the default (the sheet), as the map draws it. Fix round
 * 2 — `colour` is the configured wedge colour, which the callers read from the map config (red without one).
 * #339 — one rule in both editors: an empty width or range follows the map default (`defaults`, the map settings
 * the callers read), shown as the field's placeholder and drawn on the compass; `required` is the heading only.
 */
import { useId, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { Input } from '@/components/ui/input'
import { cn } from '@/lib/utils'
import { WEDGE_COLOUR_HEX } from '@/lib/mapSettings'
import {
  COMPASS, FOV_MAX, FOV_MIN, HEADING_MAX, HEADING_MIN, RANGE_MAX_M, RANGE_MIN_M,
  compassShare, parseDirection, sectorPath, type DirectionForm, type DirectionValues, type WedgeDefaults,
} from '@/lib/mapDirection'

// The compass: a 100 x 100 drawing, the rim 36 units from the centre stands for RANGE_MAX_M (500 m).
const CENTRE = 50
const RIM = 36

/** The wedge as typed: N at the top, the wedge from the heading and the width, its length the range on a
 *  0-500 m square-root scale (compassShare; the range is printed below). Text that is not valid keeps the last
 *  valid drawing; no heading, no wedge; an empty width or range is drawn with the defaults (#339). Screen readers
 *  get one sentence. */
function DirectionCompass({ value, required, colour, defaults }: {
  value: DirectionForm; required: boolean; colour: string; defaults: WedgeDefaults
}) {
  const { t } = useTranslation('cameras')
  const parsed = parseDirection(value, { heading: required })
  const lastValid = useRef<DirectionValues | null>(parsed)
  if (parsed) lastValid.current = parsed
  const shown = lastValid.current
  const heading = shown?.heading_degrees ?? null
  const fov = shown?.fov_degrees ?? defaults.fov
  const range = shown?.view_range_m ?? defaults.rangeM
  return (
    <div className="shrink-0">
      <svg data-direction-compass aria-hidden="true" viewBox="0 0 100 100" className="h-20 w-20 text-muted-foreground">
        <circle cx={CENTRE} cy={CENTRE} r={RIM} fill="none" stroke="currentColor" strokeWidth={1.5} />
        <text x={CENTRE} y={10} textAnchor="middle" fontSize={11} fontWeight={600} fill="currentColor">N</text>
        {heading !== null && (
          <>
            <path
              data-compass-sector
              d={sectorPath(CENTRE, CENTRE, RIM * compassShare(range), heading, fov)}
              fill={colour}
              fillOpacity={0.6}
              stroke={colour}
              strokeWidth={1}
            />
            <text data-compass-range x={CENTRE} y={98} textAnchor="middle" fontSize={10} fill="currentColor">{`${range} m`}</text>
          </>
        )}
        <circle cx={CENTRE} cy={CENTRE} r={2} fill="currentColor" />
      </svg>
      <span className="sr-only">
        {heading === null ? t('map.direction.previewNone') : t('map.direction.preview', { heading, fov, range })}
      </span>
    </div>
  )
}

export function DirectionFields({ value, onChange, defaults, disabled = false, required = false, colour = WEDGE_COLOUR_HEX.red }: {
  value: DirectionForm
  onChange: (next: DirectionForm) => void
  /** #339 — what an empty width or range follows: the map settings' defaults (lib/mapSettings wedgeDefaults), shown
   *  as the placeholders and drawn on the compass. */
  defaults: WedgeDefaults
  disabled?: boolean
  /** Fix round — the heading must hold a number (the confirm box); otherwise an empty heading clears it (the sheet).
   *  #339 — the width and the range may always be empty: they follow the default. */
  required?: boolean
  /** Fix round 2 — the compass's wedge colour: the configured wedge colour (lib/mapSettings wedgeColour). */
  colour?: string
}) {
  const { t } = useTranslation('cameras')
  const id = useId()
  const set = (patch: Partial<DirectionForm>) => onChange({ ...value, ...patch })
  const number = { type: 'number', inputMode: 'numeric', step: 1, disabled } as const

  return (
    <div className="space-y-3">
      <div className="space-y-1.5">
        <label className="text-sm font-medium" htmlFor={`${id}-heading`}>{t('map.direction.heading')}</label>
        <Input
          id={`${id}-heading`}
          {...number}
          min={HEADING_MIN}
          max={HEADING_MAX}
          value={value.heading}
          onChange={(e) => set({ heading: e.target.value })}
        />
        <div className="grid grid-cols-8 gap-1">
          {COMPASS.map((c) => {
            const on = value.heading.trim() === String(c.deg)
            return (
              <button
                key={c.label}
                type="button"
                aria-pressed={on}
                disabled={disabled}
                onClick={() => set({ heading: String(c.deg) })}
                className={cn(
                  'h-8 rounded-md border border-input text-xs font-medium transition-colors hover:bg-accent hover:text-accent-foreground disabled:pointer-events-none disabled:opacity-50',
                  on && 'border-primary bg-primary text-primary-foreground hover:bg-primary/90 hover:text-primary-foreground',
                )}
              >
                {c.label}
              </button>
            )
          })}
        </div>
      </div>
      <div className="flex items-end gap-3">
        <div className="grid flex-1 grid-cols-2 gap-2">
          <div className="space-y-1.5">
            <label className="text-sm font-medium" htmlFor={`${id}-width`}>{t('map.direction.width')}</label>
            <Input
              id={`${id}-width`}
              {...number}
              min={FOV_MIN}
              max={FOV_MAX}
              placeholder={String(defaults.fov)}
              value={value.fov}
              onChange={(e) => set({ fov: e.target.value })}
            />
          </div>
          <div className="space-y-1.5">
            <label className="text-sm font-medium" htmlFor={`${id}-range`}>{t('map.direction.range')}</label>
            <Input
              id={`${id}-range`}
              {...number}
              min={RANGE_MIN_M}
              max={RANGE_MAX_M}
              placeholder={String(defaults.rangeM)}
              value={value.range}
              onChange={(e) => set({ range: e.target.value })}
            />
          </div>
        </div>
        <DirectionCompass value={value} required={required} colour={colour} defaults={defaults} />
      </div>
    </div>
  )
}
